// Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

// NGRX
import { Store, select } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

// OLCI
import { constants } from '../../../constants';
import { GatingServices } from './gating.services';
import { ENDPOINTS, BRAND, FORGEROCK_CONFIG } from '~environments/environment';
import { FeatureFlagsService } from './feature-flags.service';
import { ResourceBundleService } from './resource-bundle.service';
import {
  Guest,
  ISailingInformationStore,
  ISailingInformationResponse,
  ReservationRetrievalObject,
  ReservationRetrievalResponse,
  ISailInformationResponse,
  ResponseError
} from '../../../models';

import * as _ from 'lodash';
import moment from 'moment';
import * as utils from '../../../util';
import * as fromRoot from '../../../reducers';
import { MicroserviceError } from '../../../models/microservice-error';

@Injectable()
export class BookingService {
	_session: ReservationRetrievalResponse;
	_content: any;
	bundle: any;
	exitModal: any;
	loggingOut: boolean;
	loggedOut: boolean;
	public sessionIsEnded: boolean;
	guests: Guest[] = [];
	checkinTokenSelect: string | any;

	constructor(
		private http: HttpClient,
		private readonly store: Store<fromRoot.AppState>,
		private bundleService: ResourceBundleService,
		private gatingService: GatingServices,
		private featureFlagsService: FeatureFlagsService,
	) {
		this.sessionIsEnded = false;
		store.pipe(select(fromRoot.getSessionVar)).subscribe(sessionVars => {
			this._session = sessionVars;
			this.loggingOut = false;
			this.loggedOut = true;
			if (this._session) {
				this.loggedOut = false;
			}
		});

		this.store.pipe(select(fromRoot.getChekinToken)).subscribe(token => (this.checkinTokenSelect = token));

		this.bundleService.content.subscribe(content => (this._content = content));
		this.bundleService.bundle.subscribe(bundle => (this.bundle = bundle));
	}

	get checkinToken() {
		return this.checkinTokenSelect;
	}

	get reservationData(): ReservationRetrievalObject | null {
		const reservationData = window.sessionStorage.getItem(constants.SESSION_STORAGE_KEYS.OLCI_RESERVATION_DATA) || null;
		return reservationData ? JSON.parse(reservationData) as ReservationRetrievalObject : null;
	}

	get snapshot() {
		return this._session;
	}

	get currencyConversionEnabled() {
		return this._session && this._session.currencyConversionEnabled ? this._session.currencyConversionEnabled : null;
	}

	get isGalapagosSailing(): boolean {
		if (!this._session) {
			return false;
		}

		return this._session.galapagosSailing;
	}

	get isSmartShip(): boolean {
		if (!this._session) {
			return false;
		}

		return this._session.enhancedSSP;
	}

	get isInternational(): boolean {
		let isDomesticBooking = true;
		this.store.pipe(select(fromRoot.getEntitySGuests)).subscribe(guests => {
			isDomesticBooking = !!guests?.find((g: any) => g.bookingOfficeCode === constants.BOOKING_OFFICE.MIA);
		});
		return !isDomesticBooking;
	}

	get isArrivalTimeEnabled(): boolean {
		if (!this._session) {
			return false;
		}

		return this._session.arrivalTimeEnabled;
	}

	get hasArrivalTimeProduct(): boolean {
		if (!this._session) {
			return false;
		}

		return this._session.hasArrivalTimeProduct;
	}

	get isBiometricConsentEnabled(): boolean {
		if (!this._session) {
			return false;
		}

		return this._session.biometricConsentEnabled;
	}

	get isProgressBarEnabled(): boolean {
		return !(this.isGalapagosSailing && !this.isSmartShip);
	}

	get sailingClosed(): boolean {
		return this._session.sailingClosed;
	}

	get isInTheStore(): boolean {
		return !!this._session;
	}

	public ipAddress(): string {
		return constants.IP_ADDRESS;
	}

	public departureDay(): string {
		return this._session.departureDate;
	}

	public isForeignGuestAddressRequired(immigrationInfo: any): boolean {
		if (!this._session) {
			return false;
		}

		return this.isUSState(this._session.destinationPort) && immigrationInfo.countryOfCitizenship !== 'USA';
	}

	public isUSState(state: string): boolean {
		let isAmericanState = false;
		if (!this._session || !this._content || !state) {
			return false;
		} else {
			let stateNoCommaArray = state.split(',');
			stateNoCommaArray = stateNoCommaArray.filter(r => r !== ' ');
			const keyToSearch = stateNoCommaArray.length > 1 ? stateNoCommaArray[1] : stateNoCommaArray[0];
			// some ports do not have a comma, for example the Singapore port is simply 'Singapore'
			// the keyToSearch could be ' Florida' or ' FL'.
			isAmericanState = this._content.statesOnlyList.some(function (info: any) {
				const stateCode = info.code.toUpperCase().trim();
				const stateName = info.name.toUpperCase().trim();
				const portCode = keyToSearch
					.toUpperCase()
					.trim()
					.split('(')[0]
					.trim();
				if (stateCode === portCode || stateName === portCode) {
					return true;
				}
			});
		}
		return isAmericanState;
	}

	public isUsaBookingOffice(bookingOffice: string) {
		return !!constants.USA_BOOKING_OFFICES.find(usaBookingOffice => usaBookingOffice === bookingOffice);
	}

	public getBrand() {
		return BRAND;
	}

	/**
	 * This returns the brand name to be added to the logo on header bars. Note
	 * that "Celebrity cruises" names is part of the logo
	 * image, for those the header brand text is empty.
	 */
	public getHeaderBrand() {
		if (this.getBrand() === constants.BRANDS_NAMES.CELEBRITY) {
			return '';
		}
		return this.getBrandCorpName();
	}

	public get isCelebrity() {
		return this.getBrand() === constants.BRANDS_NAMES.CELEBRITY;
	}

	public get isRoyal() {
		return this.getBrand() === constants.BRANDS_NAMES.ROYAL;
	}

	public getBrandCorpName() {
		let fullBrandName;
		const brandCode = this.getBrand();

		switch (brandCode) {
			case constants.BRANDS_NAMES.CELEBRITY:
				fullBrandName = 'Celebrity Cruises';
				break;
			case constants.BRANDS_NAMES.ROYAL:
			default:
				fullBrandName = 'Royal Caribbean International';
				break;
		}
		return fullBrandName;
	}

	public getBrandCorpAltText() {
		if (!this.bundle) {
			return '';
		}

		return this.isCelebrity ? this.getCelebrityName() : this.getBrandCorpName();
	}

	public getCelebrityName() {
		if (!this.bundle) {
			return 'Celebrity Cruises';
		}
		return utils.capitalizeString(this.bundle['ssp.celebrity.cruises.title']);
	}

	public get bookingId() {
		return this._session.bookingId;
	}

	public signOut(): Observable<any> {
		if (this.loggingOut || this.loggedOut) {
			return of(false);
		}
		this.loggingOut = true;
		this.loggedOut = true;
		this.gatingService.clearSessionStorage();

		return of(true);
	}

	public getEndSession() {
		if (this.sessionIsEnded) {
			return new Observable<string>();
		}
		this.sessionIsEnded = true;
		return of('Session ended successfully');
	}

	public guestAccountsClearCookies(): void {
		Object.keys(constants.COOKIES.OLCI).forEach(key => this.deleteCookie((constants.COOKIES.OLCI as any)[key]));
	}

	public deleteSessionData(): void {
		sessionStorage.clear();
		localStorage.clear();
		this.deleteAllCookies();
	}

	public guestAccountsLogout() {
		const URL = ENDPOINTS.API_POST_GUEST_ACCOUNTS_LOGOUT;
		const body = {};
		return this.http.post(URL, body, this.getHeadersAndParams()).pipe(map((res: any) => {
			return res;
		}));
	}

	private getHeadersAndParams() {
		return {
				headers: {
					'X-Requested-With': 'XMLHttpRequest',
					'Authorization': `Basic ${this.generateAuthorization(FORGEROCK_CONFIG.urlConfig.clientId, FORGEROCK_CONFIG.urlConfig.authId)}`
				},
				params: new HttpParams()
					.append('token', this.gatingService.getAccessToken())
					.append('client_id', FORGEROCK_CONFIG.urlConfig.clientId)
			};
	}

	private generateAuthorization(username: string, password: string): string {
		const encoder = new TextEncoder();
		const data = encoder.encode(`${username}:${password}`);
		const base64 = window.btoa(String.fromCharCode(...new Uint8Array(data)));
		return base64;
	}

	public getArrivalDate(): any {
		if (!this._session) {
			return;
		}
		const departureDate = moment(this._session.departureDate, constants.DATE_FORMAT.SERVER);
		let sailDays = 3;

		if (this._session.sailingDays !== null && this._session.sailingDays !== '') {
			sailDays = Number(this._session.sailingDays);
		}

		return departureDate.add(sailDays + 2, 'days');
	}

	public getArrivalTimeEmptyInventoryAlert(): string {
		const arrivalTimeMessage = _.get(this.featureFlagsService.snapshot, 'arrivalTimeMessage', false);
		return this.isSmartShip && !this.isArrivalTimeEnabled && arrivalTimeMessage ? this.bundle && this.bundle['no.arrival.times.error.message'] : '';
	}

	public mapSailingInfoToStoreFormat(sailingInfo: ISailingInformationResponse): ISailingInformationStore {
		let serverErrors: ResponseError[] = [];
		if (sailingInfo?.errors?.length) {
			serverErrors = sailingInfo.errors.map(error => this.mapMicroserviceErrorToBackendError(error, sailingInfo.status));
		}

		const mappedResponse: ISailingInformationStore = {
			status: sailingInfo.status ? sailingInfo.status : constants.HTTP_STATUS_CODE.BACKEND_ERROR,
			errors: serverErrors,
			payload: null
		};

		const sailingInfoList: ISailInformationResponse[] = sailingInfo.payload.sailingInfo;
		if (sailingInfoList.length) {
			const sailingInformation = sailingInfoList[0];
			const dateNow = moment(Date.now());
			const sailingDate = moment(sailingInformation.sailDate, constants.DATE_FORMAT.MICROSERVICE);
			const daysLeft = sailingDate.diff(dateNow, 'days');

			const sailingInfoStore = {
				shipCode: sailingInformation.shipCode,
                shipName: sailingInformation.shipName ? (sailingInformation.shipName).toUpperCase() : '',
                sailDate: sailingDate,
				transpacificNumberOfDays: sailingInformation.transpacificNumberOfDays,
                sailingStartDate: sailingDate,
                sailingEndDate: sailingInformation.sailingEndDate,
                cruiseName: sailingInformation.itinerary && sailingInformation.itinerary.description ? utils.capitalizeNameAllWords(sailingInformation.itinerary.description) : '',
				brandCode: sailingInformation.brandCode,
                sailingClosed: sailingInformation.isSailingClosed,
				departurePort: utils.capitalizeNameAllWords(sailingInformation.departurePortName),
                departurePortCode: sailingInformation.departurePort,
                departurePortName: utils.capitalizeNameAllWords(sailingInformation.departurePortName),
				departurePortCountryCode: sailingInformation.departurePortCountryCode,
                destinationPort: utils.capitalizeNameAllWords(sailingInformation.arrivalPortName),
                arrivalTimeEnabled: sailingInformation.smartShipCapabilities.isArrivalAppointmentTimesCheckinSupported,
                biometricConsentEnabled: sailingInformation.smartShipCapabilities.isBiometricConsentSupported,
                cubaSailing: sailingInformation.visitsCuba,
				currencyConversionEnabled: sailingInformation.smartShipCapabilities.isDynamicCurrencyConversionSupported,
                daysLeft: daysLeft,
                cruiseDescription: sailingInformation.itinerary && sailingInformation.itinerary.description ? utils.capitalizeNameAllWords(sailingInformation.itinerary.description) : '',
                galapagosSailing: sailingInformation.visitsGalapagos,
                hasArrivalTimeProduct: sailingInformation?.hasArrivalTimeProduct,
				bookingId: this.getBookingId(sailingInformation),
                departureDate: sailingDate.format(constants.DATE_FORMAT.SERVER),
                enhancedSSP: sailingInformation.smartShipCapabilities.isEnhancedBoardingPassSupported,
                fullCreditCard: sailingInformation.smartShipCapabilities.isFullCreditCardSupported,
                isCharterSailingSupported: sailingInformation.smartShipCapabilities.isCharterSailingSupported,
                loggedInPaxId: this.getLoggedInPaxId(sailingInformation),
                sailingDays: sailingInformation.transpacificNumberOfDays,
                securityPhoto: sailingInformation.smartShipCapabilities.isSecurityPhotoSupported,
			};
			mappedResponse.payload = sailingInfoStore;
		}
		return mappedResponse;
	}

	private getBookingId(sailingInfo: ISailInformationResponse){
        return sailingInfo?.bookingId ?? null;
    }

    private getLoggedInPaxId(sailingInfo: ISailInformationResponse){
        return sailingInfo?.loggedInPaxId ?? null;
    }

	public mapMicroserviceErrorToBackendError(error: MicroserviceError, status: number): ResponseError {
		return {
			status: status,
			statusMessage: error.internalMessage ? error.internalMessage : '',
			statusWarning: error.developerMessage ? error.developerMessage : ''
		};
	}

	private deleteCookie(name: any): void {
		// This allows to delete GA's cookies from inside OLCI page as long
		// OLCI URL is a subdomain of GA's domain.
		const domain = document.location.host.replace('www', '');
		document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:01 GMT;domain=' + domain + ';path=/;';
	}

	private deleteAllCookies(): void {
		const cookies = document.cookie.split(';');
		const paths = ['/', '/checkin'];
		const currentHost = document.location.host;
		const domainWithoutWww = currentHost.replace(/^www\./, '');
		const domainParts = domainWithoutWww.split('.');
		const rootDomain = domainParts.length >= 2 ? domainParts.slice(-2).join('.') : domainWithoutWww;
		
		const domains = [
			currentHost,
			domainWithoutWww,
			rootDomain,
			`.${currentHost}`,
			`.${domainWithoutWww}`,
			`.${rootDomain}`
		];
		
		for (const cookie of cookies) {
			const eqPos = cookie.indexOf('=');
			const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
			const cookieName = name.trim();
			
			for (const path of paths) {
				// Try without domain
				document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=${path}`;
				document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=${path};Secure`;
				
				// Try with all domain variations
				for (const domain of domains) {
					document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=${path};domain=${domain}`;
					document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=${path};domain=${domain};Secure`;
				}
			}
		}
	}
}
